import { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { resetPassword, signInWithOTP, signInWithPassword, verifyOtp } from '../libs/fetch.js';
import { useAuthContext } from '../contexts/AuthContext.jsx';
import Muffin from '../assets/primary_logo.png';
import { buttonStyle} from '../libs/sharedStyles.js';
import { primaryButtonClasses, secondaryButtonClasses } from '../libs/sharedClassNames.js';
import Loading from '../components/Loading.jsx';

export default function Signin() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [confirmationStatus, setConfirmationStatus] = useState(null); // 'verifying', 'error', 'pending', 'newLinkSent'
  const [confirmationMessage, setConfirmationMessage] = useState('');
  const [showRequestInvite, setShowRequestInvite] = useState(false);
  const [inviteRequestLoading, setInviteRequestLoading] = useState(false);
  const [pendingToken, setPendingToken] = useState(null);
  const [pendingType, setPendingType] = useState(null);
  const hasRedirected = useRef(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, loading, setLoading, organization } = useAuthContext();

  const backToSignIn = () => {
    setConfirmationStatus(null);
    setConfirmationMessage('');
    setShowRequestInvite(false);
    setErrorMessage('');
    setPendingToken(null);
    setPendingType(null);
    // Clear URL parameters
    navigate('/signin', { replace: true });
  };

  const requestNewInvite = async (event) => {
    event.preventDefault();
    if (!email) {
      setErrorMessage('Please enter your email address');
      return;
    }

    setInviteRequestLoading(true);
    setErrorMessage('');

    try {
      // Get the original type from URL to determine which method to use
      const originalType = searchParams.get('type');
      let result;

      if (originalType === 'recovery') {
        // Use resetPasswordForEmail for password recovery
        result = await resetPassword(email);
      } else {
        // Use signInWithOtp for both 'invite' and 'email' types
        result = await signInWithOTP(email);
      }

      const { error } = result;
      setConfirmationStatus('newLinkSent');
      const messageText = originalType === 'recovery' 
        ? `A new password reset link has been sent to your email address:${email}.`
        : `A new sign-in link has been sent to your email address: ${email}.`;
      setConfirmationMessage(messageText);
      setShowRequestInvite(false);

    } catch (error) {
      setErrorMessage('An error occurred while sending the email');
    } finally {
      setInviteRequestLoading(false);
    }
  };

  const handleManualVerification = async () => {
    if (!pendingToken || !pendingType) {
      setErrorMessage('No verification token found');
      return;
    }

    setConfirmationStatus('verifying');
    await handleEmailConfirmation(pendingToken, pendingType);
  };

  useEffect(() => {
    if (hasRedirected.current) return;
    
    const token = searchParams.get('token');
    const type = searchParams.get('type');
    const emailParam = searchParams.get('email');
    const returnTo = searchParams.get('returnTo');
    
    if (user && organization) {
      if (returnTo) {
        hasRedirected.current = true;
        navigate(decodeURIComponent(returnTo), { replace: true });
        return;
      }
      hasRedirected.current = true;
      navigate('/home', { replace: true });
    } else if (user && !organization) {
      // Wait for organization to load
      return;
    } else {
      setLoading(false);
      if (token && type) {
        // Instead of automatically verifying, set up for manual verification
        setPendingToken(token);
        setPendingType(type);
        setConfirmationStatus('pending');
        
        // Set up the confirmation message based on type
        let typeMessage;
        switch (type) {
          case 'invite':
            typeMessage = 'invitation';
            break;
          case 'email':
            typeMessage = 'sign-in link';
            break;
          case 'recovery':
            typeMessage = 'password reset link';
            break;
          default:
            typeMessage = 'verification link';
        }
        
        setConfirmationMessage(`Click the button below to verify your ${typeMessage}.`);
        
        // Pre-populate email if it's in the URL params
        if (emailParam && !email) {
          setEmail(emailParam);
        }        
      }
    }
  }, [user, organization, searchParams, navigate]);

  const handleEmailConfirmation = async (token, type) => {
    const emailParam = searchParams.get('email');
    try {
      const { data, error } = await verifyOtp(token, type);

      if (error) {
        setConfirmationStatus('error');
        
        // Handle expired invite tokens specifically
        if (error.message.includes('expired') || error.message.includes('invalid')) {
          
          if (type === 'invite') {
            setConfirmationMessage('Your invitation has expired.');
          } else if (type === 'email') {
            setConfirmationMessage('Your one-time link has expired or is invalid.');
          } else if (type === 'recovery') {
            setConfirmationMessage('Your recovery link has expired or is invalid.');
          }
          setShowRequestInvite(true);
        } else {
          setConfirmationMessage(error.message || `Failed to verify ${type}`);
        }
        
        setLoading(false);
        return;
      }

      if (data.user) {
        // User is now automatically signed in, clear any error status
        setConfirmationStatus(null);
        setConfirmationMessage('');
        navigate('/set-password', { replace: true });
        
        // Clear the URL parameters after successful verification
        // The AuthContext should detect the user and redirect to /home
        return;
      }

    } catch (err) {
      setConfirmationStatus('error');
      setConfirmationMessage('An unexpected error occurred');
      setLoading(false);
      console.error('Email confirmation error:', err);
    }
  };

  async function signInWithEmail(event) {
    event.preventDefault();
    const { data, error } = await signInWithPassword(email, password);
    if (error) {
      setErrorMessage(error.message);
    }
    // The useEffect will handle redirect when user and organization are loaded
  }

  // Show loading during confirmation process
  if (loading || confirmationStatus === 'verifying') {
    return <Loading />;
  }

  return (
    <div className="flex min-h-full flex-1 flex-col justify-center px-6 py-12 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-sm">
        <img
          className="mx-auto h-[6rem] w-auto"
          src={Muffin}
          alt="Muffin Data"
        />
        {!confirmationStatus && (
          <h2 className="mt-10 text-center text-2xl font-bold leading-9 tracking-tight text-gray-900">
            Sign in to your account
          </h2>
        )}
      </div>

      <div className="mt-10 sm:mx-auto sm:w-full sm:max-w-sm">
        {/* Show pending verification message */}
        {confirmationStatus === 'pending' && (
          <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-md">
            <div className="flex">
              <div className="flex-1">
                <h3 className="text-sm font-medium text-blue-800">Verification Required</h3>
                <p className="text-sm text-blue-700 mt-1">{confirmationMessage}</p>
                <div className="mt-3 flex gap-2">
                  <button
                    onClick={handleManualVerification}
                    style={buttonStyle}
                    className={`${primaryButtonClasses} text-sm`}
                  >
                    Verify Now
                  </button>
                  <button
                    onClick={backToSignIn}
                    style={buttonStyle}
                    className={`${secondaryButtonClasses} text-sm`}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {confirmationStatus === 'error' && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md">
            <div className="flex">
              <div className="text-red-600 text-xl mr-3">✗</div>
              <div className="flex-1">
                <h3 className="text-sm font-medium text-red-800">Verification Failed</h3>
                <p className="text-sm text-red-700 mt-1">{confirmationMessage}</p>
                {showRequestInvite && (
                  <div className="mt-3">
                    <p className="text-sm text-red-700 mb-2">Request a new link:</p>
                    <div className="flex gap-2 mb-3">
                      <input
                        type="email"
                        placeholder="Enter your email"
                        className="flex-1 text-sm px-3 py-1 border border-red-300 rounded focus:outline-none focus:ring-1 focus:ring-red-500"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                      />
                      <button
                        onClick={requestNewInvite}
                        disabled={inviteRequestLoading}
                        style={buttonStyle}
                        className={`${primaryButtonClasses} text-sm px-3 py-1 disabled:opacity-50`}
                      >
                        {inviteRequestLoading ? 'Sending...' : 'Send'}
                      </button>
                    </div>
                  </div>
                )}
                <button
                  onClick={backToSignIn}
                  style={buttonStyle}
                  className={`${secondaryButtonClasses} text-sm`}
                >
                  Back to Sign In
                </button>
              </div>
            </div>
          </div>
        )}
        {confirmationStatus === 'newLinkSent' && (
          <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-md">
            <div className="flex">
              <div className="text-yellow-600 text-xl mr-3">⚠️</div>
              <div className="flex-1">
                <h3 className="text-sm font-medium text-yellow-800">New Link Sent</h3>
                <p className="text-sm text-yellow-700 mt-1">{confirmationMessage}</p>
                <button
                  onClick={backToSignIn}
                  style={buttonStyle}
                  className={`${secondaryButtonClasses} text-sm mt-3`}
                >
                  Back to Sign In
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Only show the sign-in form if there's no confirmation status or if it's not pending */}
        {(!confirmationStatus) && (
          <form className="space-y-6" onSubmit={signInWithEmail}>
            <div>
              <label htmlFor="email" className="block text-sm font-medium leading-6 text-gray-900">
                Email address
              </label>
              <div className="mt-2">
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  placeholder="your-email@your-domain.com"
                  required
                  className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-green-700 sm:text-sm sm:leading-6 px-3 py-2"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between">
                <label htmlFor="password" className="block text-sm font-medium leading-6 text-gray-900">
                  Password
                </label>
              </div>
              <div className="mt-2">
                <input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="current-password"
                  placeholder="***************"
                  required
                  className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-green-700 sm:text-sm sm:leading-6 px-3 py-2"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </div>
            </div>
            
            {errorMessage && (
              <div className="mb-4 text-sm font-medium text-red-600">
                {errorMessage}
              </div>
            )}
            
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <button
                  type="submit"
                  style={buttonStyle}
                  className={primaryButtonClasses}
                >
                  Sign In
                </button>
              </div>
              <div className="text-sm leading-6">
                <a href="/forgot-password" className="font-semibold text-primaryBrandColor">
                  Forgot password?
                </a>
              </div>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}